import { memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Navigation, Bookmark } from "lucide-react";
import { useEventCover } from "../hooks/useEventCover";
import { useSaved } from "../hooks/useSaved";
import { formatCardDate } from "../lib/dates";

export const NearbyCard = memo(({ event, distanceLabel = null, index = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { src: imgSrc, onError: onImgError } = useEventCover(event);
  const { isSaved, toggle } = useSaved();
  const saved = isSaved(event.id);
  const go = () => navigate(`/event/${event.id}`, { state: { backgroundLocation: location } });

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`nearby-card-${event.id}`}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") go();
      }}
      className="group relative flex w-[250px] md:max-lg:w-[230px] shrink-0 cursor-pointer snap-start items-center gap-3 rounded-2xl border border-white/10 bg-[#141414] p-2.5 text-left rr-fade-in-x"
      style={{ animationDelay: `${index * 60}ms`, WebkitTapHighlightColor: "transparent" }}
    >
      <button
        data-testid={`save-toggle-${event.id}`}
        onClick={(e) => {
          e.stopPropagation();
          toggle(event.id);
        }}
        className="absolute right-1.5 top-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-[#050505]/70 transition-transform duration-200 active:scale-90"
        aria-label="Save event"
      >
        <Bookmark
          className={`h-[14px] w-[14px] transition-colors ${
            saved ? "fill-[#ff2a00] text-[#ff2a00]" : "text-white"
          }`}
        />
      </button>
      <div className="relative h-16 w-16 md:max-lg:h-14 md:max-lg:w-14 shrink-0 overflow-hidden rounded-xl">
        <img
          src={imgSrc}
          alt={event.name}
          loading="lazy"
          onError={onImgError}
          className="h-full w-full object-cover transition-transform duration-500 ease-out [@media(hover:hover)]:group-hover:scale-110"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#ff2a00]">
            {event.category}
          </p>
          {event.live && (
            <span
              data-testid={`nearby-live-badge-${event.id}`}
              className="flex items-center gap-1 rounded-full bg-[#ffb000] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-black"
            >
              <span className="h-1 w-1 rounded-full bg-black animate-pulse" />
              Live
            </span>
          )}
        </div>
        <h3 className="font-display truncate text-sm font-bold leading-tight text-white">
          {event.name}
        </h3>
        <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-white/50">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{event.location}</span>
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="truncate text-[10px] font-medium text-white/40">
            {formatCardDate(event)}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            <span
              data-testid={`nearby-entry-pill-${event.id}`}
              className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                event.is_free
                  ? "border-[#ff2a00]/40 bg-[#ff2a00]/10 text-[#ff2a00]"
                  : "border-white/15 bg-white/5 text-white/70"
              }`}
            >
              {event.is_free ? "Free" : "Paid"}
            </span>
            {distanceLabel && (
              <span
                data-testid={`nearby-distance-${event.id}`}
                className="flex items-center gap-1 rounded-full bg-[#ff2a00]/15 px-2 py-0.5 text-[10px] font-bold text-[#ff2a00]"
              >
                <Navigation className="h-2.5 w-2.5 fill-[#ff2a00]" />
                {distanceLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
